(function () {

    'use strict';

    var Promise = require('bluebird');
    var Base64  = require('js-base64').Base64;
    var qs      = require('qs');

    function decodeToken(jwtHelper, token) {
        var profile = jwtHelper.decodeToken(token);

        function adjustTokenProfile(profile) {
            if (profile) {

                // First process moving namespaced claims back to the root of our profile

                if ('https://sso.luminalearning.com/claims' in profile) {
                    // We have generic claims now namespaced, move all of these back to the root of the profile after checking not already set

                    // Get a list of new namespaced claims which are already set in profile
                    var duplicateClaims = Object.keys(profile['https://sso.luminalearning.com/claims']).filter(function(claim) {
                        return (claim in profile);
                    });

                    // Delete any duplicated namespaced claims
                    angular.forEach(duplicateClaims, function(claim) {
                        delete profile['https://sso.luminalearning.com/claims'][claim];
                    });

                    // Assign all new namespaced claims to the root of the profile
                    profile = Object.assign(profile, profile['https://sso.luminalearning.com/claims']);

                    // Delete the namespaced claim from the profile as we no longer need it
                    delete profile['https://sso.luminalearning.com/claims'];
                }

                // Now we have sorted the namespacing we can process other profile changes...

                if ( (!('identities' in profile)) && ('identities_min' in profile) ) {
                    // We have identities_min but not identities, this is because we needed to shrink it in Auth0 rule
                    // We want to move this back to identities for now, if we need more detail later we can fetch it from the API instead
                    profile['identities'] = profile['identities_min'];
                    delete profile['identities_min'];
                }

            }
            return profile;
        };

        return adjustTokenProfile(profile);
    };

    function fixErrorAuthResult(err) {
        // Auth0 now returns access_denied instead of invalid_user_password as per https://github.com/auth0/lock/pull/1187
        // Remap it to fix
        if (err.error === 'access_denied') {
            err.code = err.name = err.error = 'invalid_user_password';
        }

        // Copy code onto name if not set
        if (!('name' in err)) {
            if ('code' in err) {
                err.name = err.code;
            } else if ('errorDescription' in err) {
                err.name = err.errorDescription;
            } else {
                err.name = 'fallback';
            }
        }

        // Copy errorDescription onto error_description if not set or fallback to name otherwise
        if (!('error_description' in err)) {
            if ('errorDescription' in err) {
                err.error_description = err.errorDescription;
            } else {
                err.error_description = err.name;
            }
        }

        // We will map a custom third party cookie error too
        if ((err.error === 'server_error' && err.name === 'Unable to configure verification page.') || (err.error === 'invalid_request' && err.name === 'No verifier returned from client.')) {
            err.code = err.name = err.error = err.error_description = err.errorDescription = 'third_party_cookies';
        }

        return err;
    };

    function findStateForRequestedPathSearch(path, search, $state) {
        // Also in JwtRedirectController
        // Inspired by https://github.com/angular-ui/ui-router/issues/1651
        // Find which state we requested
        var states = $state.get();
        var matchedState;
        for (var i = 0; i < states.length; i++) {
            var state = states[i];
            if (state.abstract) {
                continue;
            }
            var privatePortion = state.$$state();
            var match = privatePortion.url.exec(path, search);
            if (match) {
                matchedState = { state: state, stateParams: match };
                break;
            }
        }
        return matchedState;
    };

    function findStateForRequestedLocation($location, $state) {
        return findStateForRequestedPathSearch($location.path(), $location.search(), $state);
    };

    function findUrlForRequestedState($state, requestedState) {
        var destinationStateUrl;
        if (requestedState && requestedState.state.name != 'login') {
            destinationStateUrl = $state.href(requestedState.state, requestedState.stateParams, { absolute: true });
        } else {
            destinationStateUrl = $state.href('welcome', {}, { absolute: true });
        }
        return destinationStateUrl;
    };

    module.exports = function($rootScope, $q, $state, $window, $document, $http, $cookies, $location, $timeout, authManager, jwtHelper, auth0, luminaEnv, luminaProfile, returnUrlSanitise) {
        $rootScope.isAuthServiceLoaded = false; // set to true to show body once complete

        var self = this;

        var userProfile = null;
        var localStorageProfile = localStorage.getItem('profile');
        var deferredProfile = $q.defer();

        // ---------------------

        function onAuthenticatedEvent(authResult) {
            // First logout locally
            self.logoutLocally();

            var returnUrl = Base64.decode(authResult.state); // URL we should return to

            localStorage.setItem('id_token', authResult.idToken);
            authManager.authenticate();

            // Use entire token payload as the profile
            var profile = decodeToken(jwtHelper, authResult.idToken);

            // Note, we previously did auth0.getUserInfo(authResult.accessToken
            // Now, we don't because we request profile in the scope, we store decoded jwt in profile instead, for convenience
            localStorage.setItem('profile', JSON.stringify(profile));
            deferredProfile.resolve(profile);

            // We have just come back from Auth0 (we are on our callback page) and want to redirect back to the previous page based on the state we told Auth0 when we logged in
            $timeout(function() {
                if (returnUrl != null && /^http/.test(returnUrl)) {
                    // note, might not be just this app, when other apps call the login page the redirect location is set to their return url, it redirects back then that app then does its own SSO redirect back to that app
                    $window.location.href = returnUrlSanitise(returnUrl, luminaEnv.ssoUrl);
                } else {
                    // If no returnUrl state then redirect to the welcome page instead
                    $state.go('welcome');
                }

            });
        };

        function onErrorEvent(authResult) {
            // First deal with Auth0 changes
            authResult = fixErrorAuthResult(authResult);
            console.error(authResult);

            // authResult examples: {error: "access_denied", error_description: "signup_disabled"} or {error: "access_denied", error_description: "email_not_verified"}

            // URL we should return to
            var returnUrl;
            if (!('state' in authResult)) {
                // When using renewAuth checkSession there is an issue with Auth0 not returning state despite it being in Auth0 response, error object will only contain error and error_description
                // https://github.com/auth0/auth0.js/blob/master/src/web-auth/web-message-handler.js
                // error = objectHelper.pick(eventData.event.data.response, ['error', 'error_description']);

                // As renewAuth checkSession is called from the same page, we can simply use the current page
                var requestedState = findStateForRequestedLocation($location, $state);
                returnUrl = findUrlForRequestedState($state, requestedState);
            } else {
                // Find out the original redirect location
                returnUrl = Base64.decode(authResult.state);
            }

            // Go to Lumina Login page with error
            return $timeout(function() {
                if (returnUrl != null && /^http/.test(returnUrl)) {
                    $state.go('login', { returnUrl: returnUrl });
                } else {
                    $state.go('login');
                }
            });

        };

        function authorizeCallback(err, authResult) {
            // If err is login_required we will ignore it unless on the callback page, it simply means they are not signed in yet!
            if (err && ('error' in err) && err.error == 'login_required' && (!/^\/(loginCallback|callback)/.test($location.path()))) err = null;

            if (err) {
                onErrorEvent(err); // err e.g. {error: "", error_description: ""}
            } else {
                if (authResult && authResult.idToken) {
                    onAuthenticatedEvent(authResult);
                } else if (authResult && authResult.error) {
                    onErrorEvent(authResult);
                } else {
                    // If we are currently on a protected page redirect to login page instead
                    var requestedState = findStateForRequestedLocation($location, $state);
                    if (requestedState && requestedState.state.data && ('requiresLogin' in requestedState.state.data) && requestedState.state.data.requiresLogin) {
                        $state.go('login', { returnUrl: findUrlForRequestedState($state, requestedState) });
                    }
                }
            }

            $timeout(function() {
                if (!/^\/(loginCallback|callback)/.test($location.path())) {
                    // Display page body on all pages except callback page when authorizing
                    $rootScope.isAuthServiceLoaded = true;
                }
            }, 1000);
        };

        // ---------------------

        this.logoutLocally = function() {
            deferredProfile = $q.defer();
            localStorage.removeItem('id_token');
            localStorage.removeItem('profile');
            localStorage.removeItem('ssoVersion');
            authManager.unauthenticate();
            userProfile = {};
        };

        if (localStorageProfile != null) {
            try {
                userProfile = JSON.parse(localStorageProfile);
            } catch(e) {
            }
        }
        if (userProfile == null) {
            // Ensure we clear profile and id_token from local storage if we don't have a valid profile at this point to force re-auth
            self.logoutLocally();
        }

        if (userProfile) {
            deferredProfile.resolve(userProfile);
        }
        this.getProfileDeferred = function() {
            return deferredProfile.promise;
        };
        this.getProfile = function() {
            return userProfile;
        };
        // Whenever profile is updated after first authentication then controllers can subscribe to auth-service-profile-updated to receive updates
        this.subscribeToProfileUpdates = function(scope, callback) {
            var handler = $rootScope.$on('auth-service-profile-updated', callback);
            scope.$on('$destroy', handler);
        };
        this.notifyProfileUpdate = function() {
            $rootScope.$emit('auth-service-profile-updated');
        };



        this.goToLogoutPage = function(returnUrl) {

            // First logout locally (SSO will call our logout frame in an iFrame so this isn't actually necessary in production)
            this.logoutLocally();

            if (returnUrl == 'current') {
                // If current then we will return to the current URL (like in views/header.html)
                returnUrl = $location.absUrl();
            }

            // Redirect to SSO logout to log out of all apps (this will load out logout route in an iFrame, so we don't need to do anything else now)
            var logoutUrl = 'https://sso.luminalearning.com/logout';
            if (returnUrl != null) {
                logoutUrl += '?returnUrl=' + returnUrl;
            }
            $window.location.href = logoutUrl;
        };

        this.goToLoginPage = function(returnUrl) {
            if (returnUrl == 'current') {
                // If current then we will return to the current URL
                returnUrl = $location.absUrl();
            } else if (returnUrl == null) {
                returnUrl = luminaEnv.appUrl;
            }

            // Redirect to SSO login page and ask to be redirected back
            var loginUrl = 'https://sso.luminalearning.com/login';
            if (returnUrl != null) {
                loginUrl += '?returnUrl=' + returnUrl;
            }

            $window.location.href = loginUrl;
        };

        this.waitUtilKeycloakSsoInit = function() {

            const waitForTimeoutToInit = () => {
                if (!$rootScope.hasKeycloakSsoInit) {
                    return $timeout(waitForTimeoutToInit, 100);
                }
            };

            return waitForTimeoutToInit();
        }

        this.setupKeycloakSso = function() {
            
            $window.addEventListener('message', (event) => {
                if (
                    event.origin !== luminaEnv.ssoV2Url &&
                    'string' !== typeof event.data
                ) {
                    return;
                }
                
                try {
                    let ssoData = JSON.parse(event.data);
                    
                    if ('error' in ssoData && ssoData.error) {
                        return;
                    }
                    switch (ssoData.method) {
                        case 'init':
                            $rootScope.hasKeycloakSsoInit = true;
                            break;
                        case 'set-token':
                            if (ssoData.data !== null && ssoData.data !== 'null') {
                                
                                localStorage.setItem('id_token', ssoData.data);
                                // Add profile to avoid auth0 authentication process delete token.
                                let profileString = JSON.stringify({ 
                                    keycloak: true,
                                    user_metadata: {locale: 'en-GB'}
                                });
                                // localStorage.setItem('profile', profileString);

                                // Use entire token payload as the profile
                                var profile = decodeToken(jwtHelper, ssoData.data);
                                // Note, we previously did auth0.getUserInfo(authResult.accessToken
                                // Now, we don't because we request profile in the scope, we store decoded jwt in profile instead, for convenience
                                localStorage.setItem('profile', JSON.stringify(profile));
                                deferredProfile.resolve(profile);

                                localStorage.setItem('ssoVersion', '2');
                                $rootScope.isAuthServiceLoaded = true;
                                $window.location.reload();
                            } else {
                                auth0.checkSession({
                                    state: Base64.encode($rootScope.renewAuthReturnUrl)
                                }, authorizeCallback);
                            }
                            break;
                    }
                } catch (e) {
                    console.error(e);
                }
            });

            $rootScope.$on('requestToken', () => {
                var ssoIframe = $document[0].getElementById('sso-iframe');
                ssoIframe.contentWindow.postMessage(
                    JSON.stringify({ method: 'request-token' }),
                    luminaEnv.ssoV2Url
                );
            });

        }

        this.hasValidToken = function() {
            var token = localStorage.getItem('id_token');
            if (token && token !== 'null') {
                // This happens when visiting our app for the first time, we need to check if we already have a token set and restore the auth state in our app then return to the requested page
                return !this.jwtHelper.isTokenExpired(token);
            }

            return false;
        }

        this.renewAuth = function(returnUrl) {
            $rootScope.renewAuthReturnUrl = returnUrl;
            this.waitUtilKeycloakSsoInit()
            .then(function () {
                $rootScope.$emit('requestToken');
            });
        };

        this.runAuthManager = function() {

            // To check whether the user is actually authenticated when the page is refreshed, use the checkAuthOnRefresh method in the application's run block
            $rootScope.$on('$locationChangeStart', function(event) {
                // Do not do this on the callback page, we let authenticated listener do its thing
                // Also don't do it on the logout page, we don't want to start sso when logging out
                if (!/^\/(loginCallback|callback|logout)/.test($location.path()) && !$rootScope.checkingSso) {
                    // Ensure we only check for SSO once!
                    $rootScope.checkingSso = true;
                    $timeout(function() {
                        var requestedState = findStateForRequestedLocation($location, $state);
                        var token = localStorage.getItem('id_token');
                        if (token && token !== 'null') {
                            // This happens when visiting our app for the first time, we need to check if we already have a token set and restore the auth state in our app then return to the requested page
                            if (!$rootScope.isAuthenticated) {
                                if (!jwtHelper.isTokenExpired(token)) {
                                    authManager.authenticate(); // sets $rootScope.isAuthenticated

                                    var ssoVersion = localStorage.getItem('ssoVersion');
                                    if (ssoVersion && ssoVersion == '2') {
                                        luminaProfile.whoAmIToKeycloak().$promise
                                        .then(function(result) {
                                            if ((userProfile != null) && ('locale' in result)) {
                                                // Set all updated values on userProfile
                                                userProfile = Object.assign(userProfile, result);
                                                userProfile.user_metadata = {locale: result.locale};

                                                // Update localstorage profile
                                                localStorage.setItem('profile', JSON.stringify(userProfile));

                                                // emit auth-service-profile-updated to signal userProfile has been updated to MainController
                                                self.notifyProfileUpdate();
                                            }
                                        });
                                    } else {
                                        // In the background... see if there have been any edits to their profile in the meantime
                                        luminaProfile.whoAmI().$promise
                                        .then(function(result) {
                                            if ((userProfile != null) && ('user' in result)) {
                                                // Set all updated values on userProfile
                                                userProfile = Object.assign(userProfile, result.user);

                                                // Update localstorage profile
                                                localStorage.setItem('profile', JSON.stringify(userProfile));

                                                // emit auth-service-profile-updated to signal userProfile has been updated to MainController
                                                self.notifyProfileUpdate();
                                            }
                                        });
                                    }
                                } else {
                                    // Token has expired, so delete it
                                    self.logoutLocally();
                                }
                            }
                        }
                        // Try and get token again, we might have deleted it if it was expired...
                        token = localStorage.getItem('id_token');
                        if (!token || token === 'null') {
                            // Not logged in, check single sign on
                            self.renewAuth(findUrlForRequestedState($state, requestedState));
                        } else {
                            $timeout(function() {
                                // We have a token already so display page body
                                $rootScope.isAuthServiceLoaded = true;
                            });
                        }
                    });
                }

                // Ensure we only check for SSO once! - Even if we didn't check for SSO (e.g. if visiting logout, then the portal to link-jwt, don't check SSO when going to portal later)
                $rootScope.checkingSso = true;
            });

        };

        this.interceptHash = function() {
            // Inspired by Auth0's lock.js
            // https://github.com/auth0/angular-lock/blob/eaff8f191499a717052f7268428cb7be730b0bff/dist/angular-lock.js#L73

            $rootScope.$on('$locationChangeStart', function(event, location) {
                // Set up the logic for when a user authenticates (usually on the callback page)
                if (/id_token=/.test(location) || /error=/.test(location)) {
                    // Hash simulation for html5Mode(true).
                    var hash = (window.location.hash) ? window.location.hash : '#' + location.replace(/http.?:\/\/[^/]+/,'').slice(1);

                    var parseHashOptions = {
                        hash: hash
                    };

                    // The following is needed due to redirecting to authorize from a different origin app so state is not stored as a transaction in local storage
                    // If we have state provided in callback hash, pass this to parseHash to ensure validateAuthenticationResponse always passes
                    // Note this overrides the state checks from the transaction in local storage getStoredTransaction
                    // For security maybe we need a way of finding out if a transaction is in progress and only do this if it isn't, or passing another param to our callback page?
                    // https://github.com/auth0/auth0.js/pull/708#issuecomment-373717811
                    // For now we will keep this, as in my view this has implementation issues in Auth0.js and hasn't been thought through properly, we need a better disable option
                    var parsedQs = qs.parse(hash.replace(/^#?\/?/, ''));
                    if (parsedQs.hasOwnProperty('state')) {
                        parseHashOptions.state = parsedQs.state;
                    }

                    auth0.parseHash(parseHashOptions, authorizeCallback);
                }
            });
        };

    }

})();
